import React, { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { createContext } from 'use-context-selector';
import { v4 as uuid } from 'uuid';

import { preCheckinRequest } from '@/services/PreCheckin';
import request from '@/services/request';

import { buildPresignedForm } from './buildPresignedForm';
import { FileAWSFormat, FileProps, FileReference, FileReferenceResponse } from './types';

export type IFileReferenceCustomHeader = {
  is: boolean;
  token: string;
  value: string;
};

export const initialValuesFile: FileProps = {
  id: uuid(),
  name: '',
  size: 0,
  readableSize: '0 KB',
  file: '',
  MIMEtype: '',
  previewURL: null,
  url: null,
  uploadProgress: 0,
  uploaded: false,
  error: false,
};

interface FileContextType {
  uploadedFiles: FileProps[];
  setUploadedFiles: Function;
  createFileReference: (
    params: FileReference,
    customHeader?: IFileReferenceCustomHeader,
  ) => Promise<FileReferenceResponse>;
  uploadFileToS3: Function;
  updateFile: Function;
  deleteFile: Function;
}

export const FileContext = createContext<FileContextType>({} as FileContextType);

export const FileProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [uploadedFiles, setUploadedFiles] = useState<FileProps[]>([]);

  useEffect(
    () => () => {
      uploadedFiles.forEach((file) => file.previewURL && URL.revokeObjectURL(file.previewURL));
      setUploadedFiles([]);
    },
    [],
  );

  const updateFile = useCallback((id, data) => {
    setUploadedFiles((state) =>
      state.map((file) => (file.id === id ? { ...file, ...data } : file)),
    );
  }, []);

  const deleteFile = useCallback((id: string) => {
    setUploadedFiles((state) => state.filter((file) => file.id !== id));
  }, []);

  const createFileReference = useCallback(
    async (
      params: FileReference,
      customHeader?: IFileReferenceCustomHeader,
    ): Promise<FileReferenceResponse> => {
      if (customHeader?.is) {
        preCheckinRequest.defaults.headers.common[customHeader.value] = customHeader?.token;
        const { data } = await preCheckinRequest.post('/files/', params);
        return data;
      }

      const { data } = await request.post('/files/', params);
      return data;
    },
    [],
  );

  const convertBase64ToFile = (file: FileProps) => {
    const binary = atob((file.file as string).split(',')[1]);
    const array = [];
    for (let i = 0; i < binary.length; i += 1) {
      array.push(binary.charCodeAt(i));
    }

    return new Blob([new Uint8Array(array)], {
      type: file.MIMEtype,
    });
  };

  const uploadFileToS3 = useCallback(async (file: FileProps, params: FileAWSFormat) => {
    let fileFormatted: Blob | File;

    if (typeof file?.file === 'string') {
      fileFormatted = convertBase64ToFile(file);
    } else if (file?.file instanceof File) {
      fileFormatted = file.file;
    } else {
      throw new Error('Tipo de arquivo não suportado');
    }

    // Espalha os campos assinados que o backend devolveu (`params.fields`) em vez
    // de cravar nomes de campo. O presigned POST do S3 é SigV4 e exige os campos
    // `x-amz-*`; a versão antiga mandava `AWSAccessKeyId`/`signature` (SigV2) e
    // omitia os `x-amz-*`, então o S3 recusava o upload com 403.
    const data = buildPresignedForm(fileFormatted, params);

    await axios
      .post(`${params.url}`, data, {
        onUploadProgress: (e) => {
          const progress: number = Math.round((e.loaded * 100) / e.total);
          updateFile(file.id, { uploadProgress: progress });
        },
        headers: { 'content-type': 'multipart/form-data' },
      })
      .then(() => {
        updateFile(file.id, {
          uploaded: true,
        });
      })
      .catch((err) => {
        updateFile(file.id, {
          error: true,
        });
        throw err;
      });
  }, []);

  return (
    <FileContext.Provider
      value={{
        uploadedFiles,
        setUploadedFiles,
        createFileReference,
        uploadFileToS3,
        updateFile,
        deleteFile,
      }}
    >
      {children}
    </FileContext.Provider>
  );
};
